import React from 'react';
import SurveyItem from '../../util/SurveyItem';
import Survey from '../../util/Survey';
import {
  withSurveyContext,
  withIsReadOnly,
  WithSurveyContextInjectedProps,
  WithIsReadOnlyInjectedProps,
} from '../Contexts';
import SurveyField from '../SurveyField';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';
import { FieldProps, getIn } from 'formik';
import SurveyItemContainer from '../SurveyItemContainer';
import ChoiceOptions from '../../util/ChoiceOptions';
import { CheckboxListField } from '../InputControls/SelectField';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { getTestProps } from '../../../../util/testProps';

import {
  Theme,
  WithStyles,
  createStyles,
  withStyles,
} from '@lifeomic/chroma-react/styles';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { Text } from '@lifeomic/chroma-react/components/Text';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      maxWidth: 500,
    },
    checkboxTextArea: {
      marginTop: theme.spacing(2),
    },
    checkbox: {
      marginTop: theme.spacing(2),
    },
    errorMessage: {
      color: theme.palette.error.main,
      lineHeight: 1.25,
    },
  });

export interface FieldComponentOwnProps {
  validate: (value: any) => void;
  responseAnswerType: string;
  survey: Survey;
  surveyItem: SurveyItem;
  isReadOnly: boolean;
  error: string;
  shouldShowError: boolean;
  className?: string;
  intl: IntlShape;
  isOpenChoice: boolean;
}

export type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithStyles<typeof styles>;

const messages = defineMessages({
  other: {
    id: 'radioGroup.other',
    defaultMessage: 'Other',
  },
  placeholder: {
    id: 'radioGroup.textarea.placeholder',
    defaultMessage: 'Please specify',
  },
  ariaLabel: {
    id: 'radioGroup.textarea.ariaLabel',
    defaultMessage: 'other text area',
  },
});

class FieldComponent extends React.PureComponent<FieldComponentProps> {
  state = { isOpenChoiceFocused: false, textFieldValue: null as string | null };
  choiceOptions: ChoiceOptions;

  constructor(props: FieldComponentProps) {
    super(props);

    /*
      Note that the option list is static and won't update with prop updates.
      This should be fine and reduce downstream re-renders and list generation.
      If this causes a problem, build the choices within the render method.
     */
    const { survey, surveyItem } = this.props;

    this.choiceOptions = survey.getChoiceOptionsForItem(surveyItem.link);

    // Load answer into state if it is an "Other" answer
    if (props.isOpenChoice) {
      // getIn resolves instance-qualified names inside repeating group
      // instances (e.g. "sisters[0].choices"); bracket access does not
      const values =
        (getIn(props.form.values, this.props.field.name) as string[]) ?? [];
      if (Array.isArray(values)) {
        const otherValue = values.find((value: any) =>
          value.startsWith('Other(open-choice): '),
        );
        if (otherValue) {
          const normalizedValue = otherValue.replace(
            'Other(open-choice): ',
            '',
          );
          this.state = {
            isOpenChoiceFocused: true,
            textFieldValue: normalizedValue,
          };
        }
      } else {
        console.error('Open-choice values is not an array');
      }
    }
  }

  syncOtherOption = (newValue?: any) => {
    const values =
      newValue ??
      (getIn(this.props.form.values, this.props.field.name) as string[]) ??
      [];

    values.forEach((value: any) => {
      // 1. Check if the "Other(open-choice): " option exists in the form values
      if (value.startsWith('Other(open-choice): ')) {
        // 2. If it does, remove it so it can be replaced with the new value
        values.splice(values.indexOf(value), 1);
      }
    });

    // 3. If the text field has a value, add it to the form values
    if (this.state.textFieldValue && this.state.textFieldValue !== '') {
      values.push(`Other(open-choice): ${this.state.textFieldValue}`);
    }

    // 4. Update the form values
    this.props.form.setFieldValue(this.props.field.name, values);
  };

  handleChange = (newValue: any) => {
    this.syncOtherOption(newValue);
  };

  handleChangedOther = (e: any) => {
    this.setState({ isOpenChoiceFocused: e.target.checked });
    if (!e.target.checked) {
      this.setState({ textFieldValue: '' }, () => {
        this.syncOtherOption();
      });
    }
  };

  handleChangeOpenChoiceText = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    if (event.target.value && event.target.value !== '') {
      this.setState({ textFieldValue: event.target.value }, () => {
        this.syncOtherOption();
      });
    } else {
      this.setState({ textFieldValue: null }, () => {
        this.syncOtherOption();
      });
    }
  };

  render() {
    if (this.choiceOptions === null) return null;

    const {
      field: { value },
      surveyItem,
      isReadOnly,
      shouldShowError,
      error,
      className,
      isOpenChoice,
      intl,
      classes,
    } = this.props;

    return (
      <div>
        <CheckboxListField
          className={className}
          itemType={this.choiceOptions.selectFieldType}
          value={value}
          onChange={this.handleChange}
          options={this.choiceOptions.values}
          label={surveyItem.label}
          disabled={isReadOnly}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          error={shouldShowError ? error : null}
          linkId={surveyItem.link}
          aria-label={`Input for ${surveyItem.text}`}
        />
        {
          // Render the text area if the "Other" option is selected
          !isOpenChoice ? null : (
            <div>
              <Checkbox
                onChange={this.handleChangedOther}
                label={intl.formatMessage(messages.other)}
                disabled={isReadOnly}
                checked={this.state.isOpenChoiceFocused}
                className={classes.checkbox}
                {...getTestProps(
                  getSurveyItemTestId(surveyItem.link, `checkboxOption-other`),
                )}
              />
              <TextArea
                readOnly={isReadOnly}
                className={classes.checkboxTextArea}
                hidden={!this.state.isOpenChoiceFocused}
                placeholder={intl.formatMessage(messages.placeholder)}
                onChange={e => this.handleChangeOpenChoiceText(e)}
                aria-label={intl.formatMessage(messages.ariaLabel)}
                value={this.state.textFieldValue ?? ''}
                {...getTestProps(
                  getSurveyItemTestId(
                    surveyItem.link,
                    `checkboxOption-textarea`,
                  ),
                )}
              />
            </div>
          )
        }
        {error ? (
          <Text
            size="caption"
            aria-live="assertive"
            role="alert"
            className={classes.errorMessage}
          >
            {error}
          </Text>
        ) : null}
      </div>
    );
  }
}

export interface CheckboxGroupOwnProps {
  surveyItem: SurveyItem;
  intl: IntlShape;
}

export type CheckboxGroupProps = CheckboxGroupOwnProps &
  WithIsReadOnlyInjectedProps &
  WithCheckForEnablementInjectedProps &
  WithValidationInjectedProps &
  WithSurveyContextInjectedProps &
  WithStyles<typeof styles>;

class CheckboxGroup extends React.PureComponent<CheckboxGroupProps> {
  render() {
    const {
      surveyItem,
      survey,
      isEnabled,
      shouldShowError,
      error,
      validate,
      isReadOnly,
      classes,
      intl,
    } = this.props;

    if (!isEnabled) return null;

    return (
      <SurveyItemContainer surveyItem={surveyItem}>
        <SurveyField
          className={classes.root}
          validate={validate}
          responseAnswerType={surveyItem.responseValueType}
          name={surveyItem.link}
          component={FieldComponent}
          survey={survey}
          surveyItem={surveyItem}
          isReadOnly={isReadOnly}
          error={error}
          shouldShowError={shouldShowError}
          isOpenChoice={surveyItem.type === 'open-choice'}
          intl={intl}
          classes={classes}
        />
      </SurveyItemContainer>
    );
  }
}

export default withStyles(styles)(
  withIsReadOnly(
    withCheckForEnablement(
      withValidation(
        withSurveyContext(withStyles(styles)(injectIntl(CheckboxGroup))),
      ),
    ),
  ),
);
